import React, { useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Icon, Layout, Loading } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import css from './widget.css';
import WidgetActionsMenu from './WidgetActionsMenu';
import ReportsContext from '../../../../Context/reports/ReportsContext';
import { renderDisplayComponent } from '../../../reports/results/core/DisplayMethodRegistry';
import MultiReportsWidget from './MultiReportsWidget';
import useCubeQuery from '../../../../hooks/useQuery';
import useLocalization from '../../../../helpers/useLocalization';
import useCubeMetaAPI from '../../../../hooks/useCubeMetaAPI';

// Map visualization.type (from backend) to displayMethod (registry key)
const VISUALIZATION_TYPE_MAP = {
  number: 'numLabel',
  metric: 'numLabel',
  pie: 'pieChart',
  bar: 'barChart',
  line: 'lineChart',
  area: 'areaChart',
  table: 'table',
};

function Widget({ widget, widgetName, index, onDeleteWidget, currentDashboard }) {
  const reports = useContext(ReportsContext);
  const { getLocalizedValue } = useLocalization();
  const [reportData, setReportData] = useState(null);

  const dataSource = widget?.dataSource || 'reports';
  const isMultiReport = dataSource === 'reports' &&
    (widget?.widgetReportsConfig?.reportIds?.length > 1 || widget?.reportsIds?.length > 1);

  const { response: metaResponse } = useCubeMetaAPI();

  // Build combined fieldAliasMap from all views in meta
  const fieldAliasMap = useMemo(() => {
    if (!metaResponse?.cubes) return {};
    const map = {};
    metaResponse.cubes.forEach(cube => {
      [...(cube.dimensions || []), ...(cube.measures || [])].forEach(f => {
        if (f.aliasMember) map[f.name] = f.aliasMember;
      });
    });
    return map;
  }, [metaResponse]);

  // Load report data for report-based widgets
  useEffect(() => {
    const reportId = widget?.widgetReportsConfig?.reportIds?.[0] ||
      widget?.reportsIds?.[0];
    if (reportId && reports.length > 0) {
      const report = reports.find(r => r.id === reportId);
      setReportData(report || null);
    }
  }, [reports, widget]);

  // Build query reactively using useMemo — reacts to reportData changes
  const query = useMemo(() => {
    if (isMultiReport) return null;
    if (dataSource === 'query') {
      return widget?.queryConfig?.queries?.[0] || null;
    }
    // dataSource === 'reports' — wait for reportData
    return reportData?.queryMetadata || widget?.queriesMetadata?.[0] || null;
  }, [isMultiReport, dataSource, widget, reportData]);

  const [resultSet, error, isLoading] = useCubeQuery(
    query,
    { namespace: `widget-${widget?.id}` }
  );

  const getDisplayMethod = () => {
    // 1. New schema: visualization.defaultDisplayMethod
    if (widget?.visualization?.defaultDisplayMethod) {
      return widget.visualization.defaultDisplayMethod;
    }
    // 2. Map from visualization.type
    if (widget?.visualization?.type) {
      const mapped = VISUALIZATION_TYPE_MAP[widget.visualization.type];
      if (mapped) return mapped;
    }
    // 3. From linked report
    if (reportData?.visualization?.defaultDisplayMethod) {
      return reportData.visualization.defaultDisplayMethod;
    }
    // 4. Legacy fallback
    return reportData?.defaultDisplayMethod || 'numLabel';
  };

  const renderWidgetContent = () => {
    if (isMultiReport) {
      return <MultiReportsWidget widget={widget} />;
    }

    if (error) {
      return (
        <div style={{ padding: '1rem', textAlign: 'center', color: 'red' }}>
          <Icon icon="exclamation-circle" />
          <div><FormattedMessage id="ui-reports.dashboards.widgetForm.widget.error" /></div>
        </div>
      );
    }

    if (isLoading || (dataSource === 'reports' && !reportData)) {
      return (
        <Layout className="centered full" style={{ maxWidth: '15rem', height: '8rem' }}>
          <Loading size="xlarge" />
        </Layout>
      );
    }

    if (!resultSet || !resultSet.loadResponses?.length) return null;

    return (
      <div style={{ alignItems: 'center', justifyContent: 'center' }}>
        {renderDisplayComponent(getDisplayMethod(), {
          resultSet,
          reportType: reportData?.reportType,
          fieldAliasMap,
          displaySettings: { autosize: false, maxHeight: 300 },
        })}
      </div>
    );
  };

  return (
    <Card
      id={`card-new-widget-${index}`}
      cardClass={css.cardClass}
      headerClass={css.headerClass}
      roundedBorder
      headerStart={<Icon icon={isMultiReport ? 'duplicate' : 'report'}>{widgetName}</Icon>}
      headerEnd={
        <WidgetActionsMenu
          onDeleteWidget={onDeleteWidget}
          widget={widget}
          currentDashboard={currentDashboard}
        />
      }
      marginBottom0
    >
      {renderWidgetContent()}
    </Card>
  );
}

Widget.propTypes = {
  widget: PropTypes.object.isRequired,
  widgetName: PropTypes.string,
  index: PropTypes.number.isRequired,
  onDeleteWidget: PropTypes.func.isRequired,
  currentDashboard: PropTypes.object,
};

export default Widget;
